import { z } from 'zod';

const artifactReferenceSchema = z
  .object({ space: z.string().max(32), path: z.string().max(1024) })
  .strict();

/** `POST /api/apps/views`: the URL of a view of an app, in a container, for a conversation. */
export const openAppViewRequestSchema = z
  .object({
    plugin: z.string().min(1).max(96),
    path: z.string().startsWith('/').max(512),
    container: z.enum(['main', 'side', 'dialog', 'window', 'preview', 'call']),
    conversation: z.uuid().optional(),
    item: z.string().max(256).optional(),
    artifact: artifactReferenceSchema.optional(),
  })
  .strict();
export type OpenAppViewRequest = z.infer<typeof openAppViewRequestSchema>;
export type OpenAppViewResponse = {
  url: string;
  origin: string;
  /**
   * The view works in the conversation's workspace (the conversation is saved): what its context
   * says to the app, decided here rather than guessed by the page.
   */
  workspace: boolean;
};

/** `POST /api/notifications`: marks notifications read: these, a conversation's, or all. */
export const readNotificationsRequestSchema = z.union([
  z.object({ ids: z.array(z.uuid()).min(1).max(200) }).strict(),
  z.object({ threadId: z.uuid() }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);
export type ReadNotificationsRequest = z.infer<typeof readNotificationsRequestSchema>;

/** `PUT /api/push/subscription`: a browser's Web Push subscription, as `PushSubscription.toJSON()`. */
export const pushSubscriptionRequestSchema = z
  .object({
    endpoint: z.url({ protocol: /^https$/ }).max(2048),
    keys: z
      .object({
        p256dh: z.base64url().max(256),
        auth: z.base64url().max(64),
      })
      .strip(),
  })
  .strip();
export type PushSubscriptionRequest = z.infer<typeof pushSubscriptionRequestSchema>;

/** `DELETE /api/push/subscription`: forgets a browser's subscription. */
export const pushUnsubscribeRequestSchema = z.object({ endpoint: z.url().max(2048) }).strip();

/** `GET /api/push/key`: the server's VAPID public key (`applicationServerKey`), base64url. */
export type PushKeyView = { publicKey: string };

/** The data of a Web Push message: what the service worker shows, and the event it opens. */
export type PushNotificationPayload = {
  /** The event (`/api/notifications`), opened when the notification is clicked. */
  id: string;
  title: string;
  body: string | null;
  /** Notifications of one tag replace one another: a publisher's group (or the event alone). */
  tag: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  /** The conversation it opens, beyond the notifications the app still lists. */
  threadId: string | null;
  /** When it happened: an older one never replaces a newer one of its tag. */
  createdAt: string;
  /**
   * An approval the notification answers itself (Approve / Deny): where it waits, an agent's turn
   * or, without one, an app's request answered at the core.
   */
  approval?:
    | { conversationId: string; turnId: string; requestId: string }
    | { requestId: string; turnId?: undefined };
  /** The assistant calls the user (Answer / Decline): the ring, and when it stops ringing. */
  ring?: { id: string; until: string };
};

/** `GET /api/conversations/activity`: the user's conversations whose agent is working now. */
export type ConversationActivityView = { running: string[] };

/**
 * How the user hears of an event: not at all, in the list, as a toast too, natively too, or by
 * their assistant calling them about it (`call`, the user's choice only: no type defaults to it).
 */
export type EventDeliveryView = 'off' | 'list' | 'toast' | 'native' | 'call';

/**
 * The core's types the user cannot be called about: what pages follow (a call would call again,
 * or for every change of a conversation).
 */
export const UNCALLABLE_EVENT_TYPES: readonly string[] = [
  'crowdex/assistant.ring',
  'crowdex/assistant.show',
  'crowdex/conversation.changed',
  'crowdex/workspaces.changed',
  'crowdex/notification.test',
];

/** An event of the user's (`<plugin or crowdex>/<type>`), and how they are told of it. */
export type UserEventView = {
  id: string;
  /** The plugin that published it; null for the core. */
  plugin: string | null;
  type: string;
  title: string;
  body: string | null;
  severity: 'info' | 'success' | 'warning' | 'error';
  fields: Record<string, string | number | boolean>;
  /** A route of the plugin's page. */
  link: string | null;
  /** An external page (`https://`). */
  url: string | null;
  /** The conversation it is about, and opens. */
  threadId: string | null;
  group: string | null;
  /** Fixed when it was published, from the user's settings of its type. */
  delivery: EventDeliveryView;
  createdAt: string;
  readAt: string | null;
};

/**
 * Where an event was stored: this server (`''`), or, on a machine, the linked Crowdex Cloud
 * (`'/cloud'`), whose events the machine's server relays as its own.
 */
export type EventSourceView = '' | '/cloud';

/** An event as a server streams and lists it: with where it was stored. */
export type SourcedEventView = UserEventView & { source: EventSourceView };

/** `GET /api/events/types`: the types the user may hear of, and how they hear of each. */
export type EventTypeView = {
  type: string;
  /** Who publishes it: a plugin's title, or Crowdex. */
  source: { plugin: string | null; title: string };
  title: string;
  description: string | null;
  fields: Record<string, 'string' | 'number' | 'boolean' | { enum: string[] }>;
  /** An event of it waits on the user: about a conversation, it puts it in Needs you. */
  waits: boolean;
  /** The type's own default. */
  defaultDelivery: EventDeliveryView;
  /** The user's choice, or the default. */
  delivery: EventDeliveryView;
  /** The servers that publish it (a machine and its linked cloud). */
  sources: EventSourceView[];
  /** They deliver it differently: choosing sets it on all of them. */
  mixed: boolean;
};

/** `PUT /api/events/preferences`: how the user hears of a type (`null`: its default again). */
export const eventPreferenceRequestSchema = z
  .object({
    type: z.string().min(1).max(200),
    delivery: z.enum(['off', 'list', 'toast', 'native', 'call']).nullable(),
  })
  .strict();
export type EventPreferenceRequest = z.infer<typeof eventPreferenceRequestSchema>;

/**
 * `GET`/`PUT /api/notifications/do-not-disturb`: while it holds, events are only listed (no
 * toast, system notification or call). `until` is when a timed choice ends (the page computes
 * it in the user's time zone), `null` for `on` and `off`.
 */
export const doNotDisturbSchema = z
  .object({
    choice: z.enum(['off', 'hour', 'tomorrow', 'on']),
    until: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type DoNotDisturb = z.infer<typeof doNotDisturbSchema>;

/**
 * What a plugin is to the user, derived from its manifest (never declared; `pluginKind` in
 * `@crowdex/plugin-contracts`): an `app` or an `editor` opens from All apps; a `service` acts with
 * the user's account and has only its settings page; a `provider` (models, voices) and an `agent`
 * (a coding agent) are wiring, set up elsewhere; a `skill` is instructions; a `producer` makes
 * artifacts a common viewer reads; `plugin` is anything else.
 */
export const PLUGIN_KINDS = [
  'app',
  'editor',
  'service',
  'provider',
  'agent',
  'skill',
  'producer',
  'plugin',
] as const;
export type PluginKind = (typeof PLUGIN_KINDS)[number];

/**
 * `GET /api/plugins`: an account's plugins and what the core renders for them. The manifest
 * parts are `crowdex.json`'s, as validated by the server.
 */
export type PluginListingEntry = {
  /** What it is to the user, derived from its manifest. */
  kind?: PluginKind | undefined;
  name: string;
  version: string;
  digest: string;
  enabled: boolean;
  source: 'provisioned' | 'account' | 'local';
  /** Why the app is off though turned on (an organization's policy). */
  blocked?: string | undefined;
  /** A newer build waiting for approval, and what it adds. */
  pending?: { digest: string; version: string; added: string[]; grants: string[] } | undefined;
  /** The folder a `local` app is built from. */
  localPath?: string | undefined;
  /** What installing it granted, as the prompt listed it. */
  grants: string[];
  hasApp: boolean;
  /** The Crowlingo application it brings (`service` in `crowdex.json`): shown with its origin. */
  service?: string | undefined;
  /** Where its icon (`display.icon`, a template image the core paints) loads, when it has one. */
  iconUrl?: string | undefined;
  display: {
    title: string;
    icon?: string | undefined;
    description?: string | undefined;
    category?: string | undefined;
  };
  permissions: string[];
  page?: Record<string, unknown> | undefined;
  /** The view shown during voice calls (`call` in `crowdex.json`). */
  call?: { view: string } | undefined;
  actions: Record<string, unknown>[];
  types: Record<string, unknown>[];
  /** The ACP agents it offers (`agents` in `crowdex.json`). */
  agents: string[];
  /** Its skills' resources (`skill://<app>/<skill>/SKILL.md`). */
  skills: string[];
  /** Its VS Code color themes (`themes` in `crowdex.json`), and where each file loads. */
  themes: { id: string; label: string; type: 'light' | 'dark'; url: string }[];
  settings?: Record<string, unknown> | undefined;
};

/**
 * `POST /api/apps/call`: the core calls a route of an app for what it renders itself (a page's
 * sidebar, an action's state or `call`), answered as JSON.
 */
export const callAppRequestSchema = z
  .object({
    plugin: z.string().min(1).max(96),
    route: z.string().startsWith('/').max(512),
    method: z.enum(['GET', 'POST']).default('GET'),
    input: z.unknown().optional(),
    conversation: z.uuid().optional(),
    item: z.string().max(256).optional(),
    artifact: artifactReferenceSchema.optional(),
  })
  .strict();
export type CallAppRequest = z.input<typeof callAppRequestSchema>;

/** One entry of a page's sidebar, as its route answers (`{ items }`). */
export const appSidebarItemsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().min(1).max(256),
        title: z.string().min(1).max(200),
        detail: z.string().max(200).optional(),
        icon: z.string().min(1).max(160).optional(),
        group: z.string().min(1).max(80).optional(),
        updatedAt: z.number().int().nonnegative().optional(),
      }),
    )
    .max(500),
});

/** What an action's `state` route answers. */
export const appActionStateSchema = z
  .object({
    label: z.string().max(120).optional(),
    icon: z.string().max(160).optional(),
    active: z.boolean().optional(),
    disabled: z.boolean().optional(),
    hidden: z.boolean().optional(),
  })
  .passthrough();
export type AppActionState = z.infer<typeof appActionStateSchema>;

/** `POST /api/plugins/packages`: a built package (see `scripts/build-plugins.mjs`). */
export const uploadAppPackageRequestSchema = z
  .object({
    files: z
      .array(z.object({ path: z.string().min(1).max(512), data: z.string() }).strict())
      .min(1)
      .max(2000),
  })
  .strict();
export type UploadedAppPackage = {
  digest: string;
  name: string;
  version: string;
  title: string;
  /** What installing it grants, for the prompt. */
  grants: string[];
};

/** `POST /api/plugins/install`: installs or updates a stored package, approving its grants. */
export const installAppRequestSchema = z
  .object({
    digest: z.string().regex(/^[a-f0-9]{64}$/),
    /** Exactly what the build asks; omitted for an update that may need no approval. */
    grants: z.array(z.string().max(4096)).max(512).optional(),
  })
  .strict();

/**
 * `PUT /api/artifacts/content`: a file a common viewer edits (Markdown), saved where it is; refused
 * when it changed since `version`.
 */
export const saveArtifactContentRequestSchema = z
  .object({
    space: z.enum(['workspace', 'drive']),
    path: z.string().min(1).max(1024),
    plugin: z.string().min(1).max(96),
    conversation: z.string().uuid().optional(),
    content: z.string().max(2 * 1024 * 1024),
    version: z.string().max(128).optional(),
  })
  .strict();
export type SaveArtifactContentRequest = z.infer<typeof saveArtifactContentRequestSchema>;

/** `GET /api/artifacts/index`: the account's artifacts (a conversation's with `?conversation=`). */
export type ArtifactListingEntry = {
  id: string;
  plugin: string;
  pluginTitle: string;
  type: string;
  typeTitle: string;
  space: 'workspace' | 'drive' | 'plugin';
  path: string;
  conversation: string | null;
  /** The workspace of its conversation (`workspace:`). */
  workspace: string | null;
  title: string;
  description: string | null;
  size: number;
  modifiedAt: string;
  /** The app's view of this type, if it has one; else the core's viewer by media type. */
  view: string | null;
  edit: boolean;
  publish: boolean;
  publishedRevision: number | null;
  /** The file its content is read from: its entry for a directory type, else itself. */
  entry: string;
  /**
   * Tagged `template` (shipped by its plugin, or by the user): the app's tab offers it to start
   * from, and using it duplicates it.
   */
  template: boolean;
  /** The app route that duplicates it into the open conversation's workspace, if its type has one. */
  duplicate: string | null;
  /**
   * The app route answering its compact preview (`types[].preview`), drawn in a conversation's
   * preview card instead of its view; absent, the card shows its view.
   */
  preview?: string | null;
  /** A plugin folder of the workspace (`core/plugin`): what it is, and whether it is installed. */
  pluginFolder?: PluginFolderState;
  /** Its version as the index knows it: one more each time it is seen changed. */
  version?: number;
  /**
   * A render (an MP4 of a video project): the source it was rendered from (its entry's id when
   * listed, its path in the same conversation and space) and the source's version then; `stale`
   * ("Ancien") once the source is at a later version.
   */
  derivedFrom?: { artifact: string | null; path: string; version: number; stale: boolean } | null;
  /** The media types its type renders to, and the action rendering each (`types[].outputs`). */
  outputs?: { mediaType: string; action: string }[];
};

/** The core's type of a workspace folder with a `crowdex.json`: a plugin being written there. */
export const PLUGIN_FOLDER_TYPE = 'core/plugin';

/**
 * A plugin being written in a workspace, as its card shows it: `not-installed` and `update` (it
 * asks for more than the user approved, in `added`) are installed from the card; `elsewhere` is
 * installed from another folder (installing moves it here); `unavailable` cannot be installed from
 * here (`message` says why); `invalid` does not build (`message` is the error). In the cloud a
 * folder is `unchecked` until its card builds it (building wakes its execution).
 */
export type PluginFolderState = {
  status:
    | 'unchecked'
    | 'not-installed'
    | 'installed'
    | 'update'
    | 'elsewhere'
    | 'unavailable'
    | 'invalid';
  name?: string;
  title?: string;
  version?: string;
  description?: string;
  /** What installing it lets it do (`manifestGrants`), and what an update adds. */
  grants: string[];
  added: string[];
  brings?: {
    page: boolean;
    widgets: number;
    tools: boolean;
    skills: boolean;
    mcp: number;
    connections: number;
    actions: number;
  };
  message?: string;
};

/**
 * `POST /api/artifacts/plugin`: installs (or updates) a plugin folder a conversation listed, or
 * answers what installing it would ask (without `grants`).
 */
export const pluginFolderInstallRequestSchema = z
  .object({
    conversation: z.string().min(1).max(128),
    path: z.string().min(1).max(1024),
    /** Exactly what the folder asks, as its card showed it; omitted, its state, built afresh. */
    grants: z.array(z.string().max(4096)).max(512).optional(),
  })
  .strict();

/** `PUT`/`DELETE /api/artifacts/tags`: tags or untags a `workspace:` or `drive:` artifact. */
export const artifactTagRequestSchema = z
  .object({
    /** The conversation whose workspace holds it (`workspace:`). */
    conversation: z.string().min(1).max(128).optional(),
    space: z.enum(['workspace', 'drive']),
    path: z.string().min(1).max(1024),
    tag: z.literal('template'),
  })
  .strict();
export type ArtifactTagRequest = z.infer<typeof artifactTagRequestSchema>;

export const CONVERSATION_CARD_STATUSES = [
  'info',
  'running',
  'waiting',
  'succeeded',
  'failed',
  'skipped',
] as const;
export type ConversationCardStatus = (typeof CONVERSATION_CARD_STATUSES)[number];

/**
 * A card in a conversation: a widget an app put in a conversation it started (`agents.card`), or
 * one an agent showed (`plugin` is `crowdex`), after the message `afterMessageId` (before the first
 * message when null). `widget` is where it is placed (`widgetRefSchema` of
 * `@crowdex/plugin-contracts`); `display`, how the core draws it: a data kind, or `view` with the
 * plugin's route framed with the params as its input.
 */
export type ConversationCard = {
  plugin: string;
  id: string;
  afterMessageId: string | null;
  title: string;
  summary: string | null;
  status: ConversationCardStatus;
  widget: { widget: string; params: Record<string, unknown> } & Record<string, unknown>;
  display: {
    /** A data kind, `view`, `artifact` (the artifact's own preview) or `unavailable`. */
    kind: string;
    /** A view widget's plugin and route. */
    view: { plugin: string; path: string } | null;
    /** An artifact widget's artifact. */
    artifact?: { space: string; path: string };
    format?: 'number' | 'currency' | 'percent' | 'duration';
    currency?: string;
    better?: 'up' | 'down' | 'none';
    link?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export const publishArtifactRequestSchema = z
  .object({
    conversation: z.uuid(),
    path: z.string().min(1).max(1024),
    revision: z.number().int().positive(),
  })
  .strict();

/** `POST /api/executions/:taskId/files`: files a turn changed, for the artifact index. */
export const reportedFilesRequestSchema = z
  .object({
    files: z
      .array(
        z
          .object({
            path: z.string().min(1).max(1024),
            deleted: z.boolean().optional(),
            size: z.number().int().nonnegative().optional(),
            content: z.string().max(400_000).optional(),
            encoding: z.enum(['utf8', 'base64']).optional(),
          })
          .strict(),
      )
      .max(500),
  })
  .strict();

/**
 * `POST /api/widgets/data`: a placed widget's data for the page (the widget, its params, the data
 * kind it is shown as, and the conversation showing it, whose workspace its plugin answers in).
 * The answer is a widget result (`widgetResultSchema` of `@crowdex/plugin-contracts`).
 */
export const widgetDataRequestSchema = z
  .object({
    widget: z.string().min(1).max(192),
    params: z.record(z.string().max(32), z.unknown()).default({}),
    kind: z.enum(['stat', 'line', 'bar', 'table', 'list']).optional(),
    conversation: z.uuid().optional(),
  })
  .strict();
export type WidgetDataRequest = z.input<typeof widgetDataRequestSchema>;
